// Small date helpers for week-based navigation. Keeps dependencies at zero —
// we avoid date-fns / dayjs on purpose.

const MS_PER_DAY = 86_400_000;

/** Format a Date as local ISO YYYY-MM-DD (no timezone conversion). */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse an ISO YYYY-MM-DD string as a local Date (midnight). */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** Return the Monday on/before the given date. */
export function mondayOf(d: Date): Date {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // JS: Sunday=0, Monday=1 ... Saturday=6. We want Monday=0.
  const jsDay = copy.getDay();
  const offset = (jsDay + 6) % 7; // 0 if Mon, 6 if Sun, etc.
  copy.setDate(copy.getDate() - offset);
  return copy;
}

/** ISO YYYY-MM-DD for this week's Monday (local time). */
export function thisMondayIso(): string {
  return toIsoDate(mondayOf(new Date()));
}

/** Add days to a Date, returning a new Date. */
export function addDays(d: Date, days: number): Date {
  return new Date(d.getTime() + days * MS_PER_DAY);
}

/** "13 Apr – 19 Apr 2026" for a Monday ISO date. */
export function formatWeekRange(weekStartIso: string): string {
  const start = parseIsoDate(weekStartIso);
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  const startOpts: Intl.DateTimeFormatOptions = sameMonth
    ? { day: "numeric" }
    : sameYear
      ? { day: "numeric", month: "short" }
      : { day: "numeric", month: "short", year: "numeric" };
  const endOpts: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  const fmtStart = new Intl.DateTimeFormat("en-GB", startOpts).format(start);
  const fmtEnd = new Intl.DateTimeFormat("en-GB", endOpts).format(end);
  return `${fmtStart} – ${fmtEnd}`;
}

/** Full day label: "Monday · 13 Apr". */
export function formatDayLabel(weekStartIso: string, dayIndex: number): string {
  const d = addDays(parseIsoDate(weekStartIso), dayIndex);
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
  }).format(d);
}

/** Short day label: "Mon 13". */
export function formatDayShort(weekStartIso: string, dayIndex: number): string {
  const d = addDays(parseIsoDate(weekStartIso), dayIndex);
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
  }).format(d);
}

/** "13 Apr" — day number + short month, no weekday. */
export function formatDayDateOnly(
  weekStartIso: string,
  dayIndex: number,
): string {
  const d = addDays(parseIsoDate(weekStartIso), dayIndex);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
  }).format(d);
}

/** Shift a week-start ISO by N weeks. */
export function shiftWeek(weekStartIso: string, weeks: number): string {
  return toIsoDate(addDays(parseIsoDate(weekStartIso), weeks * 7));
}

/** True if the given ISO Monday is the same as this week's Monday. */
export function isThisWeek(weekStartIso: string): boolean {
  return weekStartIso === thisMondayIso();
}

export const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;
